/**
 * DevLog Service
 *
 * Centralized logging for debugging and development.
 * Captures app events, import progress, errors, and more.
 * Events are stored in memory and can be viewed on /log page.
 */

// ============================================================
// Types
// ============================================================

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogCategory = 'import' | 'db' | 'sync' | 'ui' | 'app' | 'theme' | 'nav';

export interface LogEntry {
  id: number;
  timestamp: Date;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: unknown;
}

export interface LogStats {
  total: number;
  byLevel: Record<LogLevel, number>;
  byCategory: Record<LogCategory, number>;
}

// ============================================================
// Log Storage
// ============================================================

const MAX_LOGS = 1000; // Keep last 1000 entries
let logs: LogEntry[] = [];
let nextId = 1;
let listeners: Array<(entry: LogEntry) => void> = [];

// ============================================================
// Core Functions
// ============================================================

/**
 * Add a log entry.
 */
function addLog(
  level: LogLevel,
  category: LogCategory,
  message: string,
  data?: unknown
): LogEntry {
  const entry: LogEntry = {
    id: nextId++,
    timestamp: new Date(),
    level,
    category,
    message,
    data,
  };

  logs.push(entry);

  // Trim old entries
  if (logs.length > MAX_LOGS) {
    logs = logs.slice(-MAX_LOGS);
  }

  // Notify listeners
  listeners.forEach((listener) => {
    try {
      listener(entry);
    } catch (err) {
      console.error('[DevLog] Listener error:', err);
    }
  });

  // Also log to console in debug mode
  const isDebug =
    typeof window !== 'undefined' &&
    (import.meta.env.VITE_DEBUG === 'true' ||
      new URLSearchParams(window.location.search).has('debug'));

  if (isDebug || level === 'error' || level === 'warn') {
    const prefix = `[${category.toUpperCase()}]`;
    const consoleMethod =
      level === 'error'
        ? console.error
        : level === 'warn'
          ? console.warn
          : level === 'debug'
            ? console.debug
            : console.log;

    if (data !== undefined) {
      consoleMethod(prefix, message, data);
    } else {
      consoleMethod(prefix, message);
    }
  }

  return entry;
}

// ============================================================
// Public API
// ============================================================

export const devlog = {
  // Level-based logging
  debug: (category: LogCategory, message: string, data?: unknown) =>
    addLog('debug', category, message, data),

  info: (category: LogCategory, message: string, data?: unknown) =>
    addLog('info', category, message, data),

  warn: (category: LogCategory, message: string, data?: unknown) =>
    addLog('warn', category, message, data),

  error: (category: LogCategory, message: string, data?: unknown) =>
    addLog('error', category, message, data),

  // Get all logs
  getAll: (): LogEntry[] => [...logs],

  // Get recent logs
  getRecent: (count = 100): LogEntry[] => logs.slice(-count),

  // Get logs by category
  getByCategory: (category: LogCategory): LogEntry[] =>
    logs.filter((l) => l.category === category),

  // Get logs by level
  getByLevel: (level: LogLevel): LogEntry[] =>
    logs.filter((l) => l.level === level),

  // Get errors only
  getErrors: (): LogEntry[] => logs.filter((l) => l.level === 'error'),

  // Get warnings and errors
  getIssues: (): LogEntry[] =>
    logs.filter((l) => l.level === 'error' || l.level === 'warn'),

  // Clear all logs
  clear: () => {
    logs = [];
    nextId = 1;
    devlog.info('app', 'Logs cleared');
  },

  // Get statistics
  getStats: (): LogStats => {
    const byLevel: Record<LogLevel, number> = {
      debug: 0,
      info: 0,
      warn: 0,
      error: 0,
    };
    const byCategory: Record<LogCategory, number> = {
      import: 0,
      db: 0,
      sync: 0,
      ui: 0,
      app: 0,
      theme: 0,
      nav: 0,
    };

    logs.forEach((entry) => {
      byLevel[entry.level]++;
      byCategory[entry.category]++;
    });

    return {
      total: logs.length,
      byLevel,
      byCategory,
    };
  },

  // Subscribe to new log entries
  subscribe: (listener: (entry: LogEntry) => void): (() => void) => {
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  },

  // Export logs as JSON
  exportJSON: (): string => {
    return JSON.stringify(logs, null, 2);
  },

  // Export logs as text
  exportText: (): string => {
    return logs
      .map((entry) => {
        const ts = entry.timestamp.toISOString();
        const level = entry.level.toUpperCase().padEnd(5);
        const cat = entry.category.toUpperCase().padEnd(6);
        const dataStr = entry.data ? ` | ${JSON.stringify(entry.data)}` : '';
        return `${ts} [${level}] [${cat}] ${entry.message}${dataStr}`;
      })
      .join('\n');
  },
};

// Initialize with startup message
devlog.info('app', 'DevLog service initialized');
